<template>
  <div>
    <WarningAlert v-if="(hasMultiSigPending || otherAdultWaiversNames) && !signing">
        <p class="font-weight-bold">{{ $t("waivers.multiple_signatures_required") }}</p>
        <p>{{ $t("waivers.event_locked_until_signed") }}</p>
        <p v-if="otherAdultWaiversNames">{{ $t("waivers.adults_must_sign_own", { names: otherAdultWaiversNames }) }}</p>
      </WarningAlert>
      <v-alert
        v-if="amaBypassBanner && !signing"
        type="info"
        dense
        outlined
        class="mb-4"
      >
        {{ amaBypassBanner }}
      </v-alert>
      <ParentGuardianDialog
        v-model="parentGuardianDialogOpen"
        :adultId="userId"
        :minor="parentGuardianDialogMinor"
        :buttonColor="buttonColor"
        @confirmed="onParentGuardianConfirmed"
        @blocked="onParentGuardianBlocked"
      />
      <v-alert
        v-if="actionFeedback && !signing"
        :type="actionFeedbackType"
        dense
        class="mb-4"
      >
        {{ actionFeedback }}
      </v-alert>
      <div v-if="!signing">        
        <v-data-table 
          :headers="signableWaiversHeaders" 
          :items="waivers" 
          :items-per-page="-1"
          dense
          :footer-props="{
            'items-per-page-text': $t('dataTable.rowsPerPageText'),
            'items-per-page-all-text': $t('dataTable.rowsPerPageAllText')
          }"
          :no-data-text="$t('dataTable.noDataText')"
          :headerProps="{
            'sort-by-text': $t('dataTable.sortByText')
          }"
        >
          <template v-slot:[`item.waiverStatus`]="{ item }">
            <template v-if="item.amaBypassed">
              <v-chip small color="success" text-color="white" label>
                <v-icon small left>fa fas fa-shield-check</v-icon>
                {{ $t("memberRegistration.ama_inline.covered_by_ama") }}
              </v-chip>
              <div v-if="item.amaReleaseExpiration" class="text--secondary mt-1" style="font-size:11px;">
                {{ $t("memberRegistration.ama_inline.release_expires", { date: item.amaReleaseExpiration }) }}
              </div>
            </template>
            <template v-else-if="item.signaturesRequired > 1">
              <span v-if="item.allSignaturesComplete" style="color: green;">
                {{ $t("waivers.waiversigned") }}
              </span>
              <span v-else style="color: #b71c1c; font-weight: bold;">
                {{ item.signaturesSigned || 0 }}/{{ item.signaturesRequired }} {{ $t("waivers.waiversigned") }}
              </span>
            </template>
            <template v-else>
              {{ item.waiverStatus == 'Signed' ? $t("waivers.waiversigned") : $t("waivers.waivernotsigned") }}
            </template>
          </template>
          <template v-slot:[`item.otherSignatories`]="{ item }">
            <!-- AMA-covered waivers don't need a signature, so the
                 signatories column is intentionally blank — a pre-sign
                 pending row here would be misleading. -->
            <span v-if="item.amaBypassed"></span>
            <div v-else-if="getAllSignatories(item).length > 0" class="signatory-list">
              <div
                v-for="(sig, idx) in getAllSignatories(item)"
                :key="`${sig.userId || 'u'}-${idx}`"
                class="signatory-entry"
              >
                <v-icon
                  v-if="(sig.waiverStatus || '') === 'Signed' && resolveOtherSignatoryWaiverLink(sig, item)"
                  x-small
                  class="mr-1"
                  @click.stop="openOtherSignatoryWaiver(sig, item)"
                >
                  fa far fa-eye
                </v-icon>
                {{ sig.name }}
                <span v-if="sig.isSole" class="grey--text text--darken-1 ml-1">{{ $t("registerEvent.sole_signatory") }}</span>
                <span v-if="sig.isSelf" class="grey--text text--darken-1 ml-1">{{ $t("waivers.you_label") }}</span>
                <span v-if="(sig.waiverStatus || '') === 'Signed'" class="green--text text-caption ml-1">{{ $t("waivers.waiversigned") }}</span>
                <span v-else-if="(sig.waiverStatus || '') !== 'Rejected'" class="text-caption ml-1" style="color: #b71c1c; font-weight: bold;">{{ $t("waivers.pending") }}</span>
              </div>
            </div>
            <span v-else>-</span>
          </template>
          <template v-slot:[`item.addSignatory`]="{ item }">
            <template v-if="canSendAdultEmail(item)">
              <v-btn
                v-if="hasLivelapsEmail(item)"
                x-small
                color="orange"
                class="white--text mr-2"
                :disabled="refreshingWaivers"
                @click="openEnterEmailDialog(item)"
              >
                {{ $t("waivers.enter_email") }}
              </v-btn>
              <v-btn
                v-else-if="!cooldownRemaining[getWaiverKey(item)]"
                x-small
                :color="butonColor"
                class="white--text mr-2"
                :loading="sendingEmailByWaiver[getWaiverKey(item)] === true"
                :disabled="refreshingWaivers"
                @click="sendAdultWaiverEmail(item)"
              >
                {{ $t("waivers.send_waiver_email") }}
              </v-btn>
              <v-btn
                v-else
                x-small
                disabled
                class="mr-2"
              >
                {{ formatCooldown(cooldownRemaining[getWaiverKey(item)]) }}
              </v-btn>
            </template>
            <v-btn
              v-else-if="canShowAddSignatory(item)"
              x-small
              :color="butonColor"
              class="white--text"
              @click="openAddSignatoryModal(item)"
            >
              {{ $t("waivers.add_signatories") }}
            </v-btn>
          </template>
        </v-data-table>
      </div>
      <div v-if="waiverToSign && signing" class="text-center">
        <StepWaiverParticipant 
            ref="StepWaiverParticipantRef"
            :i="i"
            :userId="userId"
            :urlDomain="urlDomain"
            :waiver="waiverToSign"
            :organizationId="organizationId"
            :membershipType="membershipType"
            :numWaiversToSign="numWaiversToSign"
            @rejectAndCancel="rejectAndCancel"
            @getOrganizationWaiverSigned="getOrganizationWaiverSigned"
            @closeWaiver="onCloseWaiver" 
            class="mb-4"/>
      </div>

    <v-row>
      <v-col md="6" sm="6" cols="6" class="text-left">
        <v-btn :color="`${buttonColor}`" class="btn_next" @click="setStep(prevStep)">{{ $t("registerEvent.prev")
        }}</v-btn>
      </v-col>
      <v-col md="6" sm="6" cols="6" class="text-right" v-if="!signing">
        <v-btn
          outlined
          class="mr-4"
          :color="butonColor"
          :loading="refreshingWaivers"
          :disabled="refreshingWaivers"
          @click="refreshWaivers"
        >
          {{ $t("results.details.refresh") }}
        </v-btn>
        <v-btn v-if="signableWaivers.length > 0 && !signing" :color="butonColor" class="white--text me-4" :loading="generatingWaivers" @click="startSigning">
          {{ $t("waivers.signWaivers") }} <span v-if="signableWaivers.length > 1">({{signableWaivers.length}})</span>
        </v-btn>
        <v-btn :disabled="isNextStepDisabled" :color="`${buttonColor}`" class="btn_next" @click="setStep(nextStep)">{{
          $t("registerEvent.next")
        }}</v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="addSignatoryModal" :width="showNoAccountForm ? 900 : 650" persistent>
      <v-card>
        <v-card-title class="text-h6 pb-1">{{ $t("waivers.multiple_signatures_required") }}</v-card-title>
        <v-card-text>
          <p v-if="multiSigItem && multiSigItem.isMinor && (organizationName || multiSigItem.organizationName)" class="mt-0 mb-2 body-2">
            {{ organizationName || multiSigItem.organizationName }} {{ $t("waivers.requires_both_parents_signature", { minorName: multiSigMinorName }) }}
          </p>
          <p class="font-weight-bold mt-0 mb-3">{{ $t("waivers.event_locked_until_signed") }}</p>
          <p>{{ $t("waivers.x_of_y_signed", { signed: multiSigItem ? multiSigItem.signaturesSigned : 0, required: multiSigItem ? multiSigItem.signaturesRequired : 0 }) }}</p>

          <div class="mb-4">
            <p class="subtitle-1 font-weight-bold mb-2">{{ $t("waivers.current_signatories") }}</p>
            <div v-if="currentSignatories.length > 0">
              <v-chip
                v-for="sig in currentSignatories"
                :key="sig._virtual ? `virtual-${sig.userId}` : sig.waiverInstanceId"
                class="ma-1"
                :color="sig.waiverStatus === 'Signed' ? 'green' : '#b71c1c'"
                text-color="white"
                :close="!sig._virtual && sig.waiverStatus !== 'Signed'"
                @click:close="removeSignatory(sig)"
              >
                {{ sig.name }}
                <span v-if="sig.isSole" class="ml-1">{{ $t("registerEvent.sole_signatory") }}</span>
                <span v-if="sig.isSelf" class="ml-1">{{ $t("waivers.you_label") }}</span>
                <span class="ml-1 text-caption">({{ sig.waiverStatus === 'Signed' ? $t("waivers.waiversigned") : $t("waivers.pending") }})</span>
              </v-chip>
            </div>
            <p v-else class="grey--text">{{ $t("waivers.no_signatories_assigned") }}</p>
          </div>

          <v-divider class="mb-4"></v-divider>

          <p class="subtitle-1 font-weight-bold mb-2">{{ $t("waivers.add_signatory_section") }}</p>
          <p>{{ $t("waivers.select_additional_signatory") }}</p>
          <div v-if="loadingSignatories" class="text-center my-4">
            <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
            <p class="mt-2 grey--text text-caption">{{ $t("common.loading") }}</p>
          </div>
          <template v-else>
            <div v-if="!isRegistrarInCurrentSignatories" class="mt-2 mb-2">
              <v-btn
                :color="butonColor"
                class="white--text ma-1"
                @click="addRegistrarAsSignatory"
                :loading="addingSignatory"
                :disabled="addingSignatory"
              >
                {{ registrarInfo.name }}
              </v-btn>
            </div>
            <div v-if="availableSignatories.length > 0" class="mt-2">
              <v-btn
                v-for="sig in availableSignatories"
                :key="sig.userId"
                :color="butonColor"
                class="white--text ma-1"
                @click="selectSignatory(sig)"
                :loading="addingSignatory"
                :disabled="addingSignatory"
              >
                {{ sig.firstName }} {{ sig.lastName }}
              </v-btn>
            </div>
          </template>
          <div class="mt-4">
            <p>{{ $t("waivers.or_enter_email") }}</p>
            <v-text-field
              v-model="signatoryEmail"
              :label="$t('waivers.signatory_email')"
              type="email"
              outlined
              dense
            ></v-text-field>

            <SignatoryAccountStepper
              v-if="showNoAccountForm"
              :signatory-email="signatoryEmail"
              :intro-text="$t('waivers.user_not_found_collect_info')"
              :min-adult-age="Number(multiSigItem ? (multiSigItem.adultAge || 18) : 18)"
              @created="handleCreatedSignatoryAccount"
              @cancel="resetNoAccountForm"
            />
            <v-btn
              v-else
              :color="butonColor"
              class="white--text"
              @click="sendSignatoryByEmail"
              :loading="addingSignatory"
              :disabled="addingSignatory || !signatoryEmail"
            >
              {{ $t("waivers.send_waiver_email") }}
            </v-btn>
          </div>

          <v-divider class="my-4"></v-divider>

          <p v-if="multiSigItem && multiSigItem.isMinor" class="body-2">
            {{ $t("waivers.sole_guardian_explanation", { minorName: multiSigMinorName }) }}
          </p>
          <v-btn
            color="warning"
            outlined
            small
            @click="certifySoleGuardian"
            :loading="addingSignatory"
            :disabled="addingSignatory"
          >
            <v-icon left small>mdi-shield-check</v-icon>
            {{ $t("registerEvent.certify_sole_guardian") }}
          </v-btn>

          <v-alert v-if="addSignatoryError" type="error" dense class="mt-2">{{ addSignatoryError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeAddSignatoryModal">{{ $t("common.close") }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmSoleGuardianModal" max-width="500">
      <v-card>
        <v-card-title class="headline" style="word-break: break-word;">{{ $t('registerEvent.certify_sole_guardian') }}</v-card-title>
        <v-card-text>
          {{ $t("registerEvent.confirm_sole_guardian") }}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="confirmSoleGuardianModal = false">
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn color="primary" text @click="executeCertifySoleGuardian">
            {{ $t('common.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="enterEmailDialog" max-width="450" persistent>
      <v-card>
        <v-card-title style="word-break: break-word;">{{ $t('waivers.enter_email_dialog_title') }}</v-card-title>
        <v-card-text>
          <p>{{ $t('waivers.enter_email_dialog_text') }}</p>
          <v-text-field
            v-model="enterEmailValue"
            :label="$t('waivers.signatory_email')"
            type="email"
            outlined
            dense
            :error-messages="enterEmailError"
            @input="enterEmailError = ''"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeEnterEmailDialog">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            :loading="enterEmailSaving"
            :disabled="!enterEmailValue || enterEmailSaving"
            @click="submitEnterEmail"
          >
            {{ $t('common.ok') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import StepWaiverParticipant from "@/components/memberRegistration/Step_WaiverParticipant.vue";
import WarningAlert from "@/components/alert/WarningAlert";
import ParentGuardianDialog from "@/components/memberRegistration/ParentGuardianDialog.vue";
import WaiverService from "@/services/WaiverService";
import MembershipWaiverService from "@/services/MembershipWaiverService";
import EventWaiverService from "@/services/EventWaiverService";
import SignatoryAccountStepper from "@/components/waiver/SignatoryAccountStepper.vue";
import APIService from "@/services/APIService";
import ProfileService from "@/services/ProfileService";
import AuthService from "@/services/AuthService";
import { isLivelapsEmail } from "@/utils/emailUtils";

import {
  VRow,
  VCol,
  VBtn,
  VDataTable,
  VDialog,
  VCard,
  VCardTitle,
  VCardText,
  VCardActions,
  VSpacer,
  VTextField,
  VChip,
  VDivider,
  VAlert,
  VIcon,
  VProgressCircular
} from "vuetify/lib";

export default {
  components: {
    VRow,
    VCol,
    VBtn,
    StepWaiverParticipant,
    SignatoryAccountStepper,
    WarningAlert,
    ParentGuardianDialog,
    VDataTable,
    VDialog,
    VCard,
    VCardTitle,
    VCardText,
    VCardActions,
    VSpacer,
    VTextField,
    VChip,
    VDivider,
    VAlert,
    VIcon,
    VProgressCircular
  },
  props: {
    step: Number,
    buttonColor: String,
    selectedParticipants: Array,
    userId: Number,
    urlDomain: String,
    currentIp: String,
    membershipType: Object,
    refreshingWaivers: {
      type: Boolean,
      default: false
    },
    previousStep: {
      type: Number,
      default: 5
    },
    nextStepValue: {
      type: Number,
      default: 6
    },
    organizationName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      signing: false,
      waiverStep: 1,
      waivers: [],
      organizationId: 0,
      birthdate: "",
      downloadCodes: {
        adults: 1,
        minors: 2,
        everyone: 3,
      },
      signatureUUID: null,
      signature: null,
      disabledSignature: true,
      dialogSignature: false,
      dialog: false,
      checkbox: {
        agree: {
          status: false,
          updatedAt: null
        }
      },
      i: 0,
      addSignatoryModal: false,
      multiSigItem: null,
      eligibleSignatories: [],
      signatoryEmail: '',
      addingSignatory: false,
      addSignatoryError: '',
      showNoAccountForm: false,
      modalDirty: false,
      actionFeedback: null,
      actionFeedbackType: "info",
      sendingEmailByWaiver: {},
      cooldownRemaining: {},
      parentGuardianDialogOpen: false,
      parentGuardianDialogMinor: null,
      parentGuardianPendingWaivers: null,
      parentGuardianByMinor: {},
      blockedMinorIds: [],
      cooldownIntervalId: null,
      enterEmailDialog: false,
      enterEmailValue: '',
      enterEmailError: '',
      enterEmailSaving: false,
      enterEmailItem: null,
      confirmSoleGuardianModal: false,
      loadingSignatories: false,
      generatingWaivers: false,
      signingIndex: 0,
      soleGuardianWaivers: {},
      waiverPollId: null
    };
  },
  beforeDestroy() {
    this.stopCooldownTimer();
    this.stopWaiverPoll();
  },
  computed: {
    ...mapGetters({
      env: "env",
    }),
    prevStep() {
      return this.previousStep;
    },
    nextStep() {
      return this.nextStepValue;
    },
    signatureUrl() {
      return `${this.env.get("VUE_APP_ROOT")}${this.env.get(
        "VUE_APP_SIGNATURE_PATH"
      )}`;
    },
    multiSigMinorName() {
      if (!this.multiSigItem) return '';
      return `${this.multiSigItem.riderFirstName || this.multiSigItem.participantName || ''} ${this.multiSigItem.riderLastName || this.multiSigItem.participantLastname || ''}`.trim();
    },
    butonColor() {
      return this.env.get("VUE_APP_BUTTON_COLOR");
    },
    agreed() {
      return this.checkbox.agree.status;
    },
    isNextStepDisabled() {
      // AMA Waiver Bypass v2: ignore waivers that are
      //  - AMA-bypassed (read-only, counted as satisfied), or
      //  - belonging to minors blocked by 'Other' (progression already
      //    blocked for that adult/minor pair; remaining waivers should
      //    not keep the Next button disabled indefinitely).
      const blocking = this.waivers?.filter((waiver) => {
        if (waiver.amaBypassed) {
          return false;
        }
        const participantId = Number(waiver.participantId || waiver.fkUserId);
        if (this.blockedMinorIds.includes(participantId)) {
          return false;
        }
        if (waiver.signaturesRequired > 1) {
          return waiver.allSignaturesComplete !== true;
        }
        return waiver.waiverStatus !== 'Signed';
      }) || [];
      return blocking.length > 0;
    },
    /**
     * AMA Waiver Bypass v2: surface a short banner when AMA bypass has
     * covered at least one waiver for the currently selected participants.
     * Bypassed waivers stay in the rendered list (for visibility), so we
     * detect the condition by checking the per-row amaBypassed flag.
     */
    amaBypassBanner() {
      if (!this.membershipType || Number(this.membershipType.requiresValidAma) !== 1) {
        return null;
      }
      const rows = Array.isArray(this.waivers) ? this.waivers : [];
      if (rows.length === 0) return null;
      const bypassedCount = rows.filter(w => w && w.amaBypassed).length;
      if (bypassedCount === 0) return null;
      if (bypassedCount === rows.length) {
        return this.$t("memberRegistration.ama_inline.all_bypassed_banner");
      }
      return this.$t(
        "memberRegistration.ama_inline.some_bypassed_banner",
        { count: bypassedCount, total: rows.length }
      );
    },
    hasMultiSigPending() {
      return this.waivers?.some(w => w.signaturesRequired > 1 && !w.allSignaturesComplete) || false;
    },
    signableWaivers(){
      // AMA Waiver Bypass v2: bypassed waivers are read-only visibility
      // entries; exclude them from the "click to sign" list even though
      // they remain in the rendered table.
      return this.waivers?.filter((waiver) =>
        !waiver.amaBypassed &&
        waiver.canbeSigned &&
        waiver.waiverStatus == 'NotSigned'
      ) || [];
    },
    otherAdultWaiversNames()
    {
      return this.waivers?.filter((waiver) => waiver && !waiver.canbeSigned)
        .map((waiver) => {
          if (waiver.participantName || waiver.participantLastname) {
            return `${waiver.participantName} ${waiver.participantLastname}`.trim();
          }
          return '';
        })
        .filter(name => name)
        .join(', ') || '';
    },
    signableWaiversHeaders(){
      return [
        {
          text: this.$t("waivers.firstName"),
          align: "",
          sortable: false,
          value: "participantName"
        },
        {
          text: this.$t("waivers.lastName"),
          align: "",
          sortable: false,
          value: "participantLastname"
        },
        {
          text: this.$t("waivers.documentName"),
          align: "",
          sortable: false,
          value: "documentName"
        },
        {
          text: this.$t("waivers.waiverStatus"),
          align: "",
          sortable: false,
          value: "waiverStatus"
        },
        {
          text: this.$t("waivers.other_signatories"),
          align: "",
          sortable: false,
          value: "otherSignatories"
        },
        {
          text: "Actions",
          align: "",
          sortable: false,
          value: "addSignatory"
        }
      ];
    },
    waiverToSign() {
      return this.signableWaivers[0] || null;
    },
    numWaiversToSign() {
      return this.signableWaivers.length;
    },
    currentSignatories() {
      if (!this.multiSigItem) return [];
      const currentUserId = this.getCurrentUserId();
      const currentUserEmail = this.getCurrentUserEmail();
      const seen = new Set();
      const active = (this.multiSigItem.signatories || [])
        .filter(sig => sig && (sig.waiverStatus || '') !== 'Rejected');
      const result = active
        .filter(sig => {
          const key = `${sig.waiverInstanceId || 0}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        })
        .map(sig => {
          const isSelf = (currentUserId > 0 && Number(sig.userId || 0) === currentUserId) ||
                    (currentUserEmail && String(sig.email || '').toLowerCase().trim() === currentUserEmail);
          const soleKey = this.getSoleGuardianKey(this.multiSigItem);
          const isSole = !!this.soleGuardianWaivers[soleKey] && isSelf;
          return {
            ...sig,
            isSole,
            isSelf
          };
        });

      // If registrar not yet a signatory and participant is minor, show them as a virtual pending entry
      const registrarAlready = result.some(s =>
        (currentUserId > 0 && Number(s.userId || 0) === currentUserId) ||
        (currentUserEmail && String(s.email || '').toLowerCase().trim() === currentUserEmail)
      );
      if (!registrarAlready && this.isMinorMultiSigWaiver(this.multiSigItem)) {
        const soleKey = this.getSoleGuardianKey(this.multiSigItem);
        result.unshift({
          _virtual: true,
          userId: currentUserId,
          email: currentUserEmail,
          name: this.registrarInfo.name,
          waiverStatus: 'NotSigned',
          isSelf: true,
          isSole: !!this.soleGuardianWaivers[soleKey]
        });
      }

      return result;
    },
    availableSignatories() {
      const assignedEmails = new Set(
        this.currentSignatories.map(s => String(s.email || '').toLowerCase().trim()).filter(Boolean)
      );
      const assignedUserIds = new Set(
        this.currentSignatories.map(s => Number(s.userId || 0)).filter(id => id > 0)
      );
      return this.eligibleSignatories.filter(sig => {
        if (sig.isAlreadyAssigned) return false;
        if (assignedUserIds.has(Number(sig.userId || 0))) return false;
        if (assignedEmails.has(String(sig.email || '').toLowerCase().trim())) return false;
        return true;
      });
    },
    registrarInfo() {
      const id = this.getCurrentUserId();
      const email = this.getCurrentUserEmail();
      const userState = this.$store?.state?.session?.user;
      const firstName = String(userState?.firstName || '').trim();
      const lastName = String(userState?.lastName || '').trim();
      const name = `${firstName} ${lastName}`.trim();
      return { id, email, name: name || email };
    },
    isRegistrarInCurrentSignatories() {
      const { id, email } = this.registrarInfo;
      return this.currentSignatories.some(sig => {
        if ((sig.waiverStatus || '') === 'Rejected') return false;
        if (sig._virtual) return true;
        if (id > 0 && Number(sig.userId || 0) === id) return true;
        return email && String(sig.email || '').toLowerCase().trim() === email;
      });
    }
  },
  methods: {
    // Create real DB rows for any virtual (no-id) signable waivers before opening the signing UI.
    // This defers row creation until the user actually intends to sign, so removing yourself as
    // a signatory (or rejecting) won't reassign the waiver on next page load.
    /**
     * AMA Waiver Bypass v2: for each distinct minor in the pending signable
     * list, open the ParentGuardianDialog so the signer explicitly confirms
     * (or creates) the relationship before a signature is recorded.
     * Returns true if all minors are confirmed non-Other; false if a block
     * fires (the dialog will be displayed until the user resolves it).
     */
    async ensureRelationshipsForMinorWaivers() {
      // AMA Waiver Bypass v2: bypassed minor waivers don't require the
      // Parent/Guardian dialog — there is nothing to sign for that minor.
      const minorWaivers = this.signableWaivers.filter(w => w && w.isMinor && !w.amaBypassed);
      if (minorWaivers.length === 0) return true;

      const uniqueMinors = new Map();
      for (const w of minorWaivers) {
        const id = Number(w.participantId || w.fkUserId);
        if (!id) continue;
        if (!uniqueMinors.has(id)) {
          uniqueMinors.set(id, {
            id,
            fName: w.participantName || w.riderFirstName || '',
            lName: w.participantLastname || w.riderLastName || ''
          });
        }
      }

      for (const minor of uniqueMinors.values()) {
        if (this.blockedMinorIds.includes(minor.id)) return false;
        if (this.parentGuardianByMinor[minor.id] && this.parentGuardianByMinor[minor.id] !== "Other") {
          continue;
        }
        this.parentGuardianDialogMinor = minor;
        this.parentGuardianDialogOpen = true;
        const result = await new Promise(resolve => {
          this._relationshipResolver = resolve;
        });
        this._relationshipResolver = null;
        if (!result || result.blocked) return false;
        this.parentGuardianByMinor = {
          ...this.parentGuardianByMinor,
          [minor.id]: result.relationshipCode
        };
        // Selecting Guardian in the relationship dialog is an assertion of
        // sole legal custody — mirror the "Multiple signatures required"
        // sole-custody path so any multi-sig waiver for this minor
        // collapses to a single signature and the Modify Signatories
        // affordance stops showing up.
        if (result.relationshipCode === "Guardian") {
          await this.applySoleGuardianForRider(minor.id, null);
        }
      }
      return true;
    },
    onParentGuardianConfirmed(payload) {
      if (this._relationshipResolver) {
        this._relationshipResolver({ blocked: false, relationshipCode: payload.relationshipCode });
      }
    },
    onParentGuardianBlocked(payload) {
      if (payload && payload.minor && payload.minor.id) {
        this.blockedMinorIds.push(Number(payload.minor.id));
      }
      this.setActionFeedback(
        this.$t("memberRegistration.parent_guardian.blocked_message"),
        "error"
      );
      if (this._relationshipResolver) {
        this._relationshipResolver({ blocked: true, relationshipCode: "Other" });
      }
    },
    async startSigning() {
      // AMA Waiver Bypass v2: gate minor waiver signing with relationship
      // confirmation. If the user picks `Other` (or cancels), abort the
      // signing flow — the minor has been dropped from progression.
      const relationshipOk = await this.ensureRelationshipsForMinorWaivers();
      if (!relationshipOk) {
        return;
      }
      // AMA Waiver Bypass v2: copy the relationship chosen in
      // ParentGuardianDialog onto each minor waiver so Step_WaiverParticipant
      // can forward it in the sign payload. Without this the backend would
      // always receive a null signatoryRelationship for minor waivers.
      // Same treatment for isSoleCustody: the certify flow tracks the flag
      // in soleGuardianWaivers keyed by getSoleGuardianKey(item).
      for (const waiver of this.waivers || []) {
        const pid = Number(waiver.participantId || waiver.fkUserId);
        if (pid && this.parentGuardianByMinor[pid]) {
          this.$set(waiver, 'signatoryRelationship', this.parentGuardianByMinor[pid]);
        }
        const soleKey = this.getSoleGuardianKey(waiver);
        if (soleKey && this.soleGuardianWaivers && this.soleGuardianWaivers[soleKey]) {
          this.$set(waiver, 'isSoleCustody', true);
        }
      }
      this.generatingWaivers = true;
      try {
        const needsGeneration = this.signableWaivers.filter(w => !w.id);
        for (const waiver of needsGeneration) {
          const payload = {
            organizationId:           this.organizationId,
            fkOrganizationDocumentId: waiver.fkOrganizationDocumentId || waiver.membershipWaiverTemplateId,
            participantId:            waiver.participantId,
            membershipTypeName:       (this.membershipType && this.membershipType.Name) || waiver.membershipTypeName || '',
            membershipStartDate:      (this.membershipType && this.membershipType.preRegFrom) || waiver.membershipStartDate || null,
            membershipEndDate:        (this.membershipType && this.membershipType.dateTo) || waiver.membershipEndDate || null,
            ipAddress:                this.currentIp || ''
          };
          const resp = await APIService.fetch({
            method: 'post',
            path: `${this.urlDomain}/laravel/public/api/v1/participant/organization/waiver/generate`,
            data: payload
          });
          if (resp?.data?.success === 1 && resp.data.message?.id) {
            // Patch only the DB-generated fields (id, waiverLink, documentUUID) onto the
            // in-memory waiver — preserve display fields (documentMessage, etc.) from list
            const generated = resp.data.message;
            const idx = this.waivers.findIndex(w =>
              w.fkOrganizationDocumentId === waiver.fkOrganizationDocumentId &&
              w.participantId === waiver.participantId &&
              !w.id
            );
            if (idx !== -1) {
              this.$set(this.waivers, idx, {
                ...this.waivers[idx],
                id:           generated.id,
                waiverLink:   generated.waiverLink,
                documentUUID: generated.documentUUID
              });
            }
          }
        }
        this.signingIndex = 0;
        this.signing = true;
      } catch (e) {
        // If generation fails, still allow signing (row may already exist server-side)
        this.signingIndex = 0;
        this.signing = true;
      } finally {
        this.generatingWaivers = false;
      }
    },
    getWaiverKey(item) {
      return `${item?.id || item?.fkOrganizationDocumentId || item?.membershipWaiverTemplateId || "waiver"}-${item?.participantId || item?.fkUserId || item?.userId || "participant"}`;
    },
    getSoleGuardianKey(item) {
      return `${item?.fkOrganizationDocumentId || item?.membershipWaiverTemplateId || "waiver"}-${item?.participantId || item?.fkUserId || item?.userId || "participant"}`;
    },
    setActionFeedback(message, type = "info") {
      this.actionFeedback = message;
      this.actionFeedbackType = type;
    },
    clearActionFeedback() {
      this.actionFeedback = null;
    },
    async rejectAndCancel() {
      const promises = this.waivers.map(waiver => {
        if(waiver.id){
          const data = {
            "waiverStatus": "Rejected",
            "id": waiver.id
          };
          return WaiverService.rejectedWaiver(data);
        }
      });
      await Promise.all(promises);

      this.$router.push({ name: "canceled_membership" });
    },
    Initialize(data, organizationId) {
      this.waivers = data;
      this.organizationId = organizationId;
      this.initCooldowns(data);
      this.startWaiverPoll();
    },
    initCooldowns(waivers) {
      const COOLDOWN = 60;
      const now = Date.now();
      const remaining = {};
      (waivers || []).forEach((w) => {
        if (!w.lastEmailSentAt) return;
        const sent = new Date(w.lastEmailSentAt).getTime();
        if (isNaN(sent)) return;
        const elapsed = Math.floor((now - sent) / 1000);
        if (elapsed < COOLDOWN) {
          const key = this.getWaiverKey(w);
          remaining[key] = COOLDOWN - elapsed;
        }
      });
      this.cooldownRemaining = remaining;
      if (Object.keys(remaining).length > 0) {
        this.startCooldownTimer();
      }
    },
    startCooldownTimer() {
      if (this.cooldownIntervalId) return;
      this.cooldownIntervalId = setInterval(() => {
        let anyActive = false;
        const updated = { ...this.cooldownRemaining };
        Object.keys(updated).forEach((key) => {
          updated[key]--;
          if (updated[key] <= 0) {
            delete updated[key];
          } else {
            anyActive = true;
          }
        });
        this.cooldownRemaining = updated;
        if (!anyActive) {
          this.stopCooldownTimer();
        }
      }, 1000);
    },
    stopCooldownTimer() {
      if (this.cooldownIntervalId) {
        clearInterval(this.cooldownIntervalId);
        this.cooldownIntervalId = null;
      }
    },
    startWaiverPoll() {
      this.stopWaiverPoll();
      const hasUnsigned = (this.waivers || []).some(
        w => w.waiverStatus !== 'Signed' && !(w.allSignaturesComplete)
      );
      if (!hasUnsigned) return;
      this.waiverPollId = setInterval(() => {
        if (!this.signing) {
          this.refreshWaivers(false);
        }
      }, 15000);
    },
    stopWaiverPoll() {
      if (this.waiverPollId) {
        clearInterval(this.waiverPollId);
        this.waiverPollId = null;
      }
    },
    setCooldown(waiverKey, seconds) {
      this.cooldownRemaining = {
        ...this.cooldownRemaining,
        [waiverKey]: seconds
      };
      this.startCooldownTimer();
    },
    formatCooldown(seconds) {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${String(s).padStart(2, "0")}`;
    },
    async getOrganizationWaiverSigned() {
      // Sole-custody no longer creates a duplicate Signed row; the backend
      // treats a single Signed row with isSoleCustody=1 as a complete
      // multi-sig waiver. Nothing extra to do post-sign.
      this.resetWaiverStep();
      this.$emit("getOrganizationWaiverSigned");
    },
    refreshWaivers(clearFeedback = true) {
      if (clearFeedback) {
        this.clearActionFeedback();
      }
      this.getOrganizationWaiverSigned();
    },
    resetWaiverStep() {
      this.waiverStep = 1;
    },
    isMinorMultiSigWaiver(item) {
      if (!item || Number(item.signaturesRequired || 1) <= 1) return false;
      const adultAge = Number(item.adultAge || 18);
      const dob = item.participantDateOfBirth;
      if (!dob) return false;
      const today = new Date();
      const bd = new Date(dob);
      let age = today.getFullYear() - bd.getFullYear();
      const m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
      return age < adultAge;
    },
    getCurrentUserId() {
      return Number(
        this.$store?.state?.session?.user?.id ||
        this.$store?.state?.session?.userid ||
        this.userId ||
        0
      );
    },
    getCurrentUserEmail() {
      return String(
        this.$store?.state?.session?.user?.emailAddress || 
        this.$store?.state?.session?.user?.email || 
        ''
      ).toLowerCase().trim();
    },
    hasSignedWaiverTemplate(item) {
      const currentUserId = this.getCurrentUserId();
      if (!currentUserId) return false;
      return (item.signatories || []).some((signatory) => (
        Number(signatory.userId || 0) === currentUserId &&
        signatory.waiverStatus === 'Signed'
      ));
    },
    canCurrentUserSignRow(item) {
      return !!item.canbeSigned;
    },
    canSendAdultEmail(item) {
      if (!item || item.waiverStatus === 'Signed') {
        return false;
      }
      return !item.canbeSigned && Number(item.signaturesRequired || 1) <= 1;
    },
    hasLivelapsEmail(item) {
      if (!item) return false;
      const email = item.signatoryEmail || item.participantEmail || '';
      return isLivelapsEmail(email);
    },
    canShowAddSignatory(item) {
      if (!item || item.signaturesRequired <= 1 || item.allSignaturesComplete) return false;
      return this.canCurrentUserSignRow(item) || this.hasSignedWaiverTemplate(item);
    },
    async sendAdultWaiverEmail(item) {
      const waiverKey = this.getWaiverKey(item);
      const participantId = Number(item.participantId || item.fkUserId || item.userId || 0);
      const waiverId = Number(item.id || item.waiverInstanceId || item.fkOrganizationDocumentId || item.membershipWaiverTemplateId || 0);
      if (!participantId || !waiverId) {
        this.setActionFeedback(this.$t("common.error_general"), "error");
        return;
      }

      this.clearActionFeedback();
      this.sendingEmailByWaiver = {
        ...this.sendingEmailByWaiver,
        [waiverKey]: true
      };

      try {
        const response = await EventWaiverService.sendWaiver({
          email: item.signatoryEmail || item.participantEmail || "",
          waiverInstanceId: waiverId,
          userId: participantId,
          membershipWaiverTemplateId: Number(item.fkOrganizationDocumentId || item.membershipWaiverTemplateId || 0)
        });

        if (response?.data?.message === "cooldown_active") {
          const remaining = Number(response.data.secondsRemaining || 60);
          this.setCooldown(waiverKey, remaining);
          this.setActionFeedback(
            this.$t("waivers.cooldown_active", { time: this.formatCooldown(remaining) }),
            "warning"
          );
          return;
        }

        if (response?.data?.success === 1) {
          const participantName = `${item.participantName || ""} ${item.participantLastname || ""}`.trim();
          this.setActionFeedback(
            this.$t("waivers.email_sent_to_signatory", { name: participantName || item.participantEmail || "" }),
            "success"
          );
          this.setCooldown(waiverKey, 60);
          this.refreshWaivers(false);
          return;
        }

        const messageKey = response?.data?.message?.emailError || response?.data?.message;
        const translationKey = `waivers.${messageKey}`;
        this.setActionFeedback(
          messageKey && this.$te(translationKey) ? this.$t(translationKey) : this.$t("common.error_general"),
          "error"
        );
      } catch (error) {
        this.setActionFeedback(this.$t("common.error_general"), "error");
      } finally {
        this.sendingEmailByWaiver = {
          ...this.sendingEmailByWaiver,
          [waiverKey]: false
        };
      }
    },
    isMinorParticipant(item) {
      const adultAge = Number(item.adultAge || 18);
      const dob = item.participantDateOfBirth;
      if (!dob) return false;
      const today = new Date();
      const bd = new Date(dob);
      let age = today.getFullYear() - bd.getFullYear();
      const m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
      return age < adultAge;
    },
    getAllSignatories(item) {
      const currentUserId = this.getCurrentUserId();
      const currentUserEmail = this.getCurrentUserEmail();
      const active = (item?.signatories || []).filter(s => s && (s.waiverStatus || '') !== 'Rejected');
      const seen = new Set();
      const signatories = [];
      const soleKey = this.getSoleGuardianKey(item);
      active.forEach((signatory) => {
        const name = String(signatory.name || '').trim();
        if (!name) return;
        const key = `${Number(signatory.userId || 0)}:${name.toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        const isSelf = (currentUserId > 0 && Number(signatory.userId || 0) === currentUserId) ||
                       (currentUserEmail && String(signatory.email || '').toLowerCase().trim() === currentUserEmail);
        const isSole = !!this.soleGuardianWaivers[soleKey] && isSelf;
        signatories.push({
          ...signatory,
          name,
          isSelf,
          isSole
        });
      });

      // For minor multi-sig waivers, inject registrar as virtual pending signatory if not present
      const registrarAlready = signatories.some(s =>
        (currentUserId > 0 && Number(s.userId || 0) === currentUserId) ||
        (currentUserEmail && String(s.email || '').toLowerCase().trim() === currentUserEmail)
      );
      if (!registrarAlready && this.isMinorMultiSigWaiver(item)) {
        signatories.unshift({
          _virtual: true,
          userId: currentUserId,
          email: currentUserEmail,
          name: this.registrarInfo.name,
          waiverStatus: 'NotSigned',
          isSelf: true,
          isSole: !!this.soleGuardianWaivers[soleKey]
        });
      }

      // For single-signature waivers with no signatories array, show who signs
      if (signatories.length === 0 && Number(item.signaturesRequired || 1) <= 1) {
        if (this.isMinorParticipant(item)) {
          signatories.push({
            _virtual: true,
            userId: currentUserId,
            name: this.registrarInfo.name,
            waiverStatus: item.waiverStatus === 'Signed' ? 'Signed' : 'NotSigned',
            isSelf: true,
            isSole: false
          });
        } else {
          const participantName = `${item.participantName || ''} ${item.participantLastname || ''}`.trim();
          if (participantName) {
            signatories.push({
              _virtual: true,
              userId: item.participantId || item.fkUserId || item.userId,
              name: participantName,
              waiverStatus: item.waiverStatus === 'Signed' ? 'Signed' : 'NotSigned',
              isSelf: false,
              isSole: false
            });
          }
        }
      }

      return signatories;
    },
    resolveOtherSignatoryWaiverLink(signatory, item) {
      if (!signatory) return '';
      if (signatory.waiverLink) return signatory.waiverLink;

      const signatories = item?.signatories || [];
      const byInstance = signatories.find((s) =>
        Number(s.waiverInstanceId || 0) > 0 &&
        Number(s.waiverInstanceId || 0) === Number(signatory.waiverInstanceId || -1) &&
        !!s.waiverLink
      );
      if (byInstance?.waiverLink) return byInstance.waiverLink;

      const byUser = signatories.find((s) =>
        Number(s.userId || 0) === Number(signatory.userId || -1) &&
        (s.waiverStatus || '') !== 'Rejected' &&
        !!s.waiverLink
      );
      if (byUser?.waiverLink) return byUser.waiverLink;

      if (item?.waiverLink && Number(item?.fkUserId || item?.signUserId || 0) === Number(signatory.userId || -1)) {
        return item.waiverLink;
      }
      return '';
    },
    openOtherSignatoryWaiver(signatory, item) {
      const waiverLink = this.resolveOtherSignatoryWaiverLink(signatory, item);
      if (!waiverLink) return;
      const host = this.$store.getters.env.get("VUE_APP_LIVELAPS_HOST");
      window.open(`${host}${waiverLink}`, "_blank");
    },
    isMinor(date) {
      var today = new Date();
      var bd = new Date(date);
      var edad = today.getFullYear() - bd.getFullYear();
      var m = today.getMonth() - bd.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) {
        edad--;
      }
      return edad < 18;
    },
    nextWaiverStep(n) {
      if (n === this.steps) {
        this.waiverStep = 1;
      } else {
        this.waiverStep = n + 1;
      }
    },
    setStep(step) {
      this.$emit("nextStep", step);
    },
    getImage(image) {
      this.signature = image;
    },
    changeBandSignature({ value }) {
      this.disabledSignature = value;
    },
    getSignatureImage(signatureImage) {
      return 'data:image/png;base64,' + signatureImage
    },
    setSignature(index, signatureImage) {
      this.signatureUUID = 'edit'
      this.$refs.signatures[index].fromDataURL(this.getSignatureImage(signatureImage));
    },
    resetSignature(index) {
      this.$refs.signatures[index].cleanCanvas();
    },
    cancelSignature(index) {
      this.dialog = false;
      this.signatureUUID = null;
      this.resetSignature(index);
    },
    onCloseWaiver() {
      // signing=true is maintained; waiverToSign always shows signableWaivers[0].
      // After refresh, the just-signed waiver is removed from signableWaivers so
      // the next unsigned one rises to index 0 automatically.
      // The numWaiversToSign watcher exits signing mode when the count hits 0.
    },
    resetNoAccountForm() {
      this.showNoAccountForm = false;
    },
    async openAddSignatoryModal(item) {
      this.multiSigItem = item;
      this.signatoryEmail = '';
      this.addSignatoryError = '';
      this.eligibleSignatories = [];
      this.modalDirty = false;
      this.loadingSignatories = true;
      this.resetNoAccountForm();
      this.addSignatoryModal = true;
      try {
        const response = await MembershipWaiverService.getEligibleSignatories({
          riderUserId: item.participantId || item.fkUserId || item.userId,
          membershipWaiverTemplateId: item.fkOrganizationDocumentId || item.fkMembershipWaiverId || item.membershipWaiverTemplateId,
          includeAlreadyAssigned: true
        });
        if (response.data && response.data.success === 1) {
          this.eligibleSignatories = response.data.message || [];
        }
      } catch (e) {
        this.eligibleSignatories = [];
      }
      this.loadingSignatories = false;
    },
    translateWaiverError(errKey, item = null) {
      const key = Array.isArray(errKey) ? errKey[0] : errKey;
      if (key === "signatory_must_be_adult") {
        return this.$t("waivers.signatory_must_be_adult", { age: Number(item?.adultAge || 18) });
      }

      const translationKey = `waivers.${key}`;
      return (key && this.$te(translationKey)) ? this.$t(translationKey) : (key || "Error");
    },
    async removeSignatory(sig) {
      if (!this.multiSigItem || !sig.waiverInstanceId) return;
      this.addingSignatory = true;
      this.addSignatoryError = '';
      try {
        const response = await MembershipWaiverService.removeSignatory({
          waiverInstanceId: sig.waiverInstanceId,
          riderUserId: this.multiSigItem.participantId || this.multiSigItem.fkUserId || this.multiSigItem.userId
        });
        if (response.data && response.data.success === 1) {
          const soleKey = this.getSoleGuardianKey(this.multiSigItem);
          const next = { ...this.soleGuardianWaivers };
          delete next[soleKey];
          this.soleGuardianWaivers = next;
          this.modalDirty = true;
          this.$emit("getOrganizationWaiverSigned");
        } else {
          const errKey = response.data?.message;
          this.addSignatoryError = errKey
            ? this.translateWaiverError(errKey, this.multiSigItem)
            : this.$t("waivers.signatory_remove_failed");
        }
      } catch (e) {
        this.addSignatoryError = this.$t("waivers.signatory_remove_failed");
      }
      this.addingSignatory = false;
    },
    async selectSignatory(sig) {
      this.signatoryEmail = sig.email;
      await this.sendSignatoryByEmail();
    },
    certifySoleGuardian() {
      this.confirmSoleGuardianModal = true;
    },
    async executeCertifySoleGuardian() {
      if (!this.multiSigItem) {
        this.confirmSoleGuardianModal = false;
        this.addSignatoryError = this.$t("common.error_general");
        return;
      }
      this.confirmSoleGuardianModal = false;
      this.addSignatoryError = '';

      const riderId = this.multiSigItem.participantId || this.multiSigItem.fkUserId || this.multiSigItem.userId;
      await this.applySoleGuardianForRider(riderId, this.multiSigItem);
      this.addSignatoryModal = false;
    },
    /**
     * Flags all multi-sig waivers for this rider as sole-custody locally
     * so the UI hides Modify Signatories and the sign payload will carry
     * isSoleCustody=true. Invoked from ParentGuardianDialog's "Guardian"
     * selection and from the "Multiple signatures required" sole-custody
     * confirmation.
     *
     * The sole-custody completion is evaluated server-side from the flag
     * on the single Signed row — no duplicate row is needed (previously
     * this also called createSoleGuardianCopy to insert a "-2" clone).
     *
     * For rows already signed without the flag (legacy or pre-selection),
     * we still patch the existing row so the backend can recompute
     * completion without waiting for another sign event.
     */
    async applySoleGuardianForRider(riderId, fallbackItem) {
      const pendingItems = (this.waivers || []).filter(w => {
        const wRider = w.participantId || w.fkUserId || w.userId;
        return Number(wRider) === Number(riderId)
          && Number(w.signaturesRequired || 1) > 1
          && !w.allSignaturesComplete;
      });
      const targets = pendingItems.length > 0
        ? pendingItems
        : (fallbackItem ? [fallbackItem] : []);
      if (targets.length === 0) return;

      for (const item of targets) {
        this.$set(this.soleGuardianWaivers, this.getSoleGuardianKey(item), true);
      }

      const alreadySigned = targets.filter(item => this.hasSignedWaiverTemplate(item));
      if (alreadySigned.length > 0) {
        // Flag any existing Signed rows so completion recomputes to true
        // right away, without requiring another sign.
        this.addingSignatory = true;
        try {
          for (const item of alreadySigned) {
            await this.markExistingWaiverSoleCustody(item);
          }
          this.modalDirty = true;
          this.$emit("getOrganizationWaiverSigned");
        } finally {
          this.addingSignatory = false;
        }
      }

      this.waivers = [...this.waivers];
    },
    /**
     * Calls the waiver-update endpoint to set isSoleCustody=1 on the
     * already-Signed row for this rider+document. Used when a user
     * declares sole custody after they (or another signatory) had
     * already signed without the flag.
     */
    async markExistingWaiverSoleCustody(item) {
      const signedInstance = (item.signatories || []).find(s => s && s.waiverStatus === 'Signed');
      if (!signedInstance || !signedInstance.waiverInstanceId) return;
      try {
        await MembershipWaiverService.updateWaiverSoleCustody({
          organizationWaiverSignedId: signedInstance.waiverInstanceId
        });
      } catch (e) {
        // Non-fatal — the local flag still hides Modify Signatories, and
        // the next manual refresh or sign event recomputes completion.
      }
    },
    async createSoleGuardianCopy(waiver) {
      const currentUserEmail = this.getCurrentUserEmail();
      if (!currentUserEmail) return;
      try {
        await MembershipWaiverService.addAnotherSignatory({
          membershipWaiverTemplateId: waiver.fkOrganizationDocumentId || waiver.fkMembershipWaiverId || waiver.membershipWaiverTemplateId,
          riderUserId: waiver.participantId || waiver.fkUserId || waiver.userId,
          signatoryEmail: currentUserEmail,
          isSoleGuardian: true
        });
      } catch (e) {
        // Copy creation failed — user will still see the signed waiver;
        // the second signature slot can be managed via the modal.
      }
    },
    async sendSignatoryByEmail() {
      if (!this.signatoryEmail || !this.multiSigItem) return;
      this.addingSignatory = true;
      this.addSignatoryError = '';
      this.resetNoAccountForm();
      try {
        const item = this.multiSigItem;
        const response = await MembershipWaiverService.addAnotherSignatory({
          membershipWaiverTemplateId: item.fkOrganizationDocumentId || item.fkMembershipWaiverId || item.membershipWaiverTemplateId,
          riderUserId: item.participantId || item.fkUserId || item.userId,
          signatoryEmail: this.signatoryEmail
        });
        if (response.data && response.data.success === 1) {
          const soleKey = this.getSoleGuardianKey(item);
          if (this.soleGuardianWaivers[soleKey]) {
            const next = { ...this.soleGuardianWaivers };
            delete next[soleKey];
            this.soleGuardianWaivers = next;
          }
          this.modalDirty = true;
          this.$emit("getOrganizationWaiverSigned");
          this.signatoryEmail = '';
        } else {
          const errKey = response.data?.message;
          if (errKey === 'user_not_found_collect_info') {
            this.showNoAccountForm = true;
            this.addSignatoryError = '';
          } else {
            this.addSignatoryError = this.translateWaiverError(errKey, this.multiSigItem);
          }
        }
      } catch (e) {
        this.addSignatoryError = 'Error';
      }
      this.addingSignatory = false;
    },
    async addRegistrarAsSignatory() {
      const { email } = this.registrarInfo;
      if (!email) return;
      this.signatoryEmail = email;
      await this.sendSignatoryByEmail();
    },
    async handleCreatedSignatoryAccount(payload) {
      this.signatoryEmail = payload?.email || this.signatoryEmail;
      this.resetNoAccountForm();
      await this.sendSignatoryByEmail();
    },
    closeAddSignatoryModal() {
      this.addSignatoryModal = false;
      if (this.modalDirty) {
        this.$emit("getOrganizationWaiverSigned");
      }
    },
    patchWaiverEmailsForUser(userId, newEmail) {
      if (!this.waivers || !userId) return;
      for (const w of this.waivers) {
        const wUserId = Number(w.participantId || w.fkUserId || w.userId || 0);
        if (wUserId === userId) {
          if (isLivelapsEmail(w.signatoryEmail)) this.$set(w, 'signatoryEmail', newEmail);
          if (isLivelapsEmail(w.participantEmail)) this.$set(w, 'participantEmail', newEmail);
        }
      }
      this.waivers = [...this.waivers];
    },
    openEnterEmailDialog(item) {
      this.enterEmailItem = item;
      this.enterEmailValue = '';
      this.enterEmailError = '';
      this.enterEmailSaving = false;
      this.enterEmailDialog = true;
    },
    closeEnterEmailDialog() {
      this.enterEmailDialog = false;
      this.enterEmailItem = null;
      this.enterEmailValue = '';
      this.enterEmailError = '';
    },
    async submitEnterEmail() {
      const email = (this.enterEmailValue || '').trim();
      if (!email) return;

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        this.enterEmailError = this.$t('waivers.enter_email_dialog_text');
        return;
      }

      this.enterEmailSaving = true;
      this.enterEmailError = '';

      try {
        const dupResp = await AuthService.preventDuplicateEmail(email);
        if (dupResp.data && dupResp.data.match) {
          this.enterEmailError = this.$t('waivers.email_already_in_use');
          this.enterEmailSaving = false;
          return;
        }

        const userId = Number(
          this.enterEmailItem.participantId ||
          this.enterEmailItem.fkUserId ||
          this.enterEmailItem.userId ||
          0
        );
        if (!userId) {
          this.enterEmailError = this.$t('common.error_general');
          this.enterEmailSaving = false;
          return;
        }

        const authUserId = this.getCurrentUserId();
        const updateResp = await ProfileService.updateEmail({
          userId,
          email,
          authUserId
        });

        if (updateResp.data && updateResp.data.success === 1) {
          if (updateResp.data.message === 'unique_email') {
            this.enterEmailError = this.$t('waivers.email_already_in_use');
            this.enterEmailSaving = false;
            return;
          }
          this.patchWaiverEmailsForUser(userId, email);
          this.setActionFeedback(this.$t('waivers.email_updated_success'), 'success');
          this.closeEnterEmailDialog();
          this.$emit("getOrganizationWaiverSigned");
        } else {
          this.enterEmailError = this.$t('common.error_general');
        }
      } catch (e) {
        this.enterEmailError = this.$t('common.error_general');
      }
      this.enterEmailSaving = false;
    },
  },
  watch: {
    numWaiversToSign(newVal) {
      if (newVal === 0) {
        this.signing = false;
        this.signingIndex = 0;
        const justSigned = this.waivers?.find(w => w.signaturesRequired > 1 && w.waiverStatus === 'Signed' && !w.allSignaturesComplete);
        if (justSigned) {
          this.openAddSignatoryModal(justSigned);
        }
      }
    },
    step(newStep) {
      if (newStep === 3.06) {
        this.startWaiverPoll();
      } else {
        this.stopWaiverPoll();
      }
    },
    waivers: {
      handler(newWaivers) {
        if (this.multiSigItem && this.addSignatoryModal) {
          const updated = (newWaivers || []).find(w =>
            (w.fkOrganizationDocumentId || w.fkMembershipWaiverId || w.membershipWaiverTemplateId) ===
            (this.multiSigItem.fkOrganizationDocumentId || this.multiSigItem.fkMembershipWaiverId || this.multiSigItem.membershipWaiverTemplateId) &&
            (w.participantId || w.fkUserId || w.userId) ===
            (this.multiSigItem.participantId || this.multiSigItem.fkUserId || this.multiSigItem.userId)
          );
          if (updated) {
            this.multiSigItem = updated;
          }
        }
        const allDone = (newWaivers || []).every(
          w => w.waiverStatus === 'Signed' && (w.allSignaturesComplete !== false)
        );
        if (allDone) {
          this.stopWaiverPoll();
        }
      },
      deep: true
    }
  }
};
</script>
<style>
.green-alert .v-alert__border {
  color: #3c763d !important;
}
.signatory-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.signatory-entry {
  display: flex;
  align-items: center;
}
</style>
